import { API_URL } from '../../config';
import Swal from 'sweetalert2';

export const fetchPageData = async (pageId) => {
    const response = await fetch(`${API_URL}/pages-builder/by-id/${pageId}`);
    const data = await response.json();
    return data;
};


export const fetchHeaderFooterData = async (pageId) => {
    const response = await fetch(`${API_URL}/header-footer-builder/by-id/${pageId}`);
    const data = await response.json();
    return data;
};


/**
 * Build the payload for saving page data.
 * @param {string} pageId - The ID of the page.
 * @param {Array} rows - The rows of the page.
 * @returns {Object} - The constructed page data payload.
 */
const buildPageData = (pageId, rows) => ({
    title: 'My New Page',
    pageId,
    rows: rows.map(row => ({
        disabled: row.disabled,
        styles: row.styles,
        sections: row.sections.map(section => ({
            disabled: section.disabled,
            layoutKey: section.columns.map(col => col.class).join('-'),
            columns: section.columns.map(col => ({
                class: col.class,
                disabled: col.disabled,
                styles: col.styles,
                components: col.components.map(component => ({
                    type: component.type,
                    props: component.props,
                    disabled: component.disabled,
                })),
                subsections: col.sections?.map(nestedSection => ({
                    layoutKey: nestedSection.columns.map(nestedCol => nestedCol.class).join('-'),
                    disabled: nestedSection.disabled,
                    styles: nestedSection.styles,
                    subcolumns: nestedSection.columns.map(nestedCol => ({
                        class: nestedCol.class,
                        disabled: nestedCol.disabled,
                        styles: nestedCol.styles,
                        components: nestedCol.components.map(nestedComponent => ({
                            type: nestedComponent.type,
                            props: nestedComponent.props,
                            disabled: nestedComponent.disabled,
                        })),
                    })),
                })) || [],
            })),
        })),
    })),
});

/**
 * Save page data to the server.
 * @param {string} pageId - The page ID.
 * @param {Array} rows - The rows of the page.
 * @returns {Promise} - A promise that resolves with the server response.
 */
export const savePageData = async (pageId, rows) => {
    const pageData = buildPageData(pageId, rows);

    try {
        const response = await fetch(`${API_URL}/pages-builder`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pageData),
        });

        if (!response.ok) {
            throw new Error('Failed to save page data');
        }

        return await response.json();
    } catch (error) {
        console.error('Error saving page data:', error);
        throw error;
    }
};

/**
 * Save page data to the server.
 * @param {string} pageId - The page ID.
 * @param {Array} rows - The rows of the page.
 * @returns {Promise} - A promise that resolves with the server response.
 */
export const saveHeaderFooterData = async (pageId, rows) => {
    const pageData = buildPageData(pageId, rows);

    try {
        const response = await fetch(`${API_URL}/header-footer-builder`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pageData),
        });

        if (!response.ok) {
            throw new Error('Failed to save page data');
        }

        return await response.json();
    } catch (error) {
        console.error('Error saving page data:', error);
        throw error;
    }
};



export const fetchPostData = async (postId, subdomain) => {
    const response = await fetch(`${API_URL}/post-builder/by-id/${postId}?account_key=${subdomain}`);
    const data = await response.json();
    return data;
};

/**
 * Build the payload for saving page data.
 * @param {string} pageId - The ID of the page.
 * @param {Array} rows - The rows of the page.
 * @returns {Object} - The constructed page data payload.
 */
const buildPostData = (postId, rows) => ({
    title: 'New Post',
    postId,
    rows: rows.map(row => ({
        disabled: row.disabled,
        styles: row.styles,
        sections: row.sections.map(section => ({
            disabled: section.disabled,
            layoutKey: section.columns.map(col => col.class).join('-'),
            columns: section.columns.map(col => ({
                class: col.class,
                disabled: col.disabled,
                styles: col.styles,
                components: col.components.map(component => ({
                    type: component.type,
                    props: component.props,
                    disabled: component.disabled,
                })),
                subsections: col.sections?.map(nestedSection => ({
                    layoutKey: nestedSection.columns.map(nestedCol => nestedCol.class).join('-'),
                    disabled: nestedSection.disabled,
                    styles: nestedSection.styles,
                    subcolumns: nestedSection.columns.map(nestedCol => ({
                        class: nestedCol.class,
                        disabled: nestedCol.disabled,
                        styles: nestedCol.styles,
                        components: nestedCol.components.map(nestedComponent => ({
                            type: nestedComponent.type,
                            props: nestedComponent.props,
                            disabled: nestedComponent.disabled,
                        })),
                    })),
                })) || [],
            })),
        })),
    })),
});

/**
 * Save page data to the server.
 * @param {string} pageId - The page ID.
 * @param {Array} rows - The rows of the page.
 * @returns {Promise} - A promise that resolves with the server response.
 */
export const savePostData = async (postId, rows) => {
    const postData = buildPostData(postId, rows);

    try {
        const response = await fetch(`${API_URL}/post-builder`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(postData),
        });

        if (!response.ok) {
            throw new Error('Failed to save post data');
        }

        return await response.json();
    } catch (error) {
        console.error('Error saving post data:', error);
        throw error;
    }
};


/**
 * Save a single component as a reusable template.
 * Prompts the user for a template name (using SweetAlert2) and then posts to the backend.
 *
 * @param {Object} component - The component object containing `type` and `props`.
 */
export const handleSaveAsTemplate = async (component, subdomain) => {
  try {
    const { value: templateName } = await Swal.fire({
      title: 'Enter a name for this template',
      input: 'text',
      inputPlaceholder: 'My Template',
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) {
          return 'Template name cannot be empty!';
        }
        return null;
      },
    });

    // If user cancels or doesn't input a name, templateName will be undefined
    if (!templateName) return;

    // Construct the data you want to send to the server
    const templateData = {
      name: templateName,
      type: component.type,
      props: component.props,
      // Include any additional fields your backend requires
    };

    // Make the POST request to your backend
    const response = await fetch(`${API_URL}/templates?account_key=${subdomain}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // If your API needs authorization, include it here:
        // Authorization: 'Bearer <your-token>'
      },
      body: JSON.stringify(templateData),
    });

    if (!response.ok) {
      throw new Error(`Failed to save template: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Template saved successfully:', result);

    // Alert success with SweetAlert2
    Swal.fire({
      icon: 'success',
      title: 'Template saved!',
      text: `Template "${templateName}" saved successfully.`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error('Error saving template:', error);
    Swal.fire({
      icon: 'error',
      title: 'Oops...',
      text: 'Failed to save template. Check console for details.',
    });
  }
};


export const handleSaveRowAsTemplate = async (row, subdomain) => {
  try {
    const { value: templateName } = await Swal.fire({
      title: 'Enter a name for this row template',
      input: 'text',
      inputPlaceholder: 'My 2-Column Hero Row',
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) {
          return 'Template name cannot be empty!';
        }
      },
    });

    if (!templateName) return;

    // Prepare the data
    const rowTemplateData = {
      name: templateName,
      row_data: row, // The entire row object 
    };

    // POST to your backend
    const response = await fetch(`${API_URL}/row-templates?account_key=${subdomain}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rowTemplateData),
    });

    if (!response.ok) {
      throw new Error('Failed to save row template');
    }

    const result = await response.json();
    console.log('Row template saved successfully:', result);

    Swal.fire({
      icon: 'success',
      title: 'Row Template saved!',
      text: `Row template "${templateName}" saved successfully.`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error('Error saving row template:', error);
    Swal.fire({
      icon: 'error',
      title: 'Oops...',
      text: 'Failed to save row template. Check console for details.',
    });
  }
};


/**
 * Send a chat message to the AI builder assistant.
 *
 * @param {Object} params
 * @param {string} params.message       - The user's message
 * @param {string|number} params.pageId - Current page ID
 * @param {Array}  params.history       - Conversation history [{role, content}]
 * @param {Array}  params.pageSnapshot  - Flattened page snapshot from flattenPageForAI()
 * @param {Object} [params.siteContext] - Site metadata { site_name, business_type, key_services, language }
 * @returns {Promise<{actions: Array, summary: string}>}
 */
export const sendChatBuilderMessage = async ({ message, pageId, history, pageSnapshot, siteContext }) => {
  const response = await fetch(`${API_URL}/chat/builder-action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      page_id: pageId,
      history,
      page_snapshot: pageSnapshot,
      site_context: siteContext || {},
    }),
  });

  // Always parse JSON — error responses also contain a useful summary message
  const data = await response.json().catch(() => ({
    actions: [],
    summary: `Server error (${response.status}). Please try again.`,
  }));

  if (!response.ok) {
    // Return as normal response so chat shows the message instead of crashing
    return {
      actions: [],
      summary: data.summary || `Error ${response.status}. Please try again.`,
    };
  }

  return data;
};


/**
 * Generate an image via DALL-E 3 through the backend, saves to media library.
 * @param {Object} params
 * @param {string} params.description  - What to generate
 * @param {string} params.accountKey   - Account subdomain
 * @param {string} [params.context]    - Optional context (e.g. "for a gallery about travel")
 * @returns {Promise<{url: string, media_id: number, title: string}>}
 */
export const generateAiImage = async ({ description, accountKey, context }) => {
  const response = await fetch(`${API_URL}/chat/generate-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description, account_key: accountKey, context }),
  });
  if (!response.ok) throw new Error(`Image generation failed: ${response.status}`);
  return response.json();
};

/**
 * Upload an image file from the chat UI to the media library.
 * @param {File}   file        - The image file
 * @param {string} accountKey  - Account subdomain
 * @returns {Promise<{url: string, media_id: number, title: string}>}
 */
export const uploadChatImage = async (file, accountKey) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('account_key', accountKey);

  const response = await fetch(`${API_URL}/chat/upload-image`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
  return response.json();
};


/**
 * Fetch account media items for the image picker gallery tab.
 * @param {string} accountKey
 * @returns {Promise<Array>}
 */
export const fetchMediaItems = async (accountKey) => {
  const response = await fetch(`${API_URL}/media?account_key=${accountKey}`);
  if (!response.ok) throw new Error('Failed to fetch media');
  const data = await response.json();
  return Array.isArray(data.media) ? data.media : [];
};


/**
 * Apply a global theme to the website via CSS variables saved to custom_styles.
 * @param {Object} params
 * @param {string} params.accountKey
 * @param {string} [params.primaryColor]
 * @param {string} [params.secondaryColor]
 * @param {string} [params.textColor]
 * @param {string} [params.backgroundColor]
 * @returns {Promise}
 */
export const applyGlobalTheme = async ({ accountKey, primaryColor, secondaryColor, textColor, backgroundColor }) => {
  const response = await fetch(`${API_URL}/chat/apply-theme`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      account_key: accountKey,
      primary_color: primaryColor || null,
      secondary_color: secondaryColor || null,
      text_color: textColor || null,
      background_color: backgroundColor || null,
    }),
  });
  if (!response.ok) throw new Error(`Theme apply failed: ${response.status}`);
  return response.json();
};


/**
 * Start AI page generation via queue job.
 * @param {Object} params
 * @param {string} params.description  - What the page should contain
 * @param {string} params.accountKey   - Account subdomain
 * @param {Object} [params.siteContext] - { site_name, business_type, key_services }
 * @returns {Promise<{tracking_id, page_title, message}>}
 */
export const createPageFromChat = async ({ description, accountKey, siteContext }) => {
  const response = await fetch(`${API_URL}/chat/create-page`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      description,
      account_key: accountKey,
      site_context: siteContext || {},
    }),
  });
  const data = await response.json().catch(() => ({ error: `Server error ${response.status}` }));
  if (!response.ok) throw new Error(data.error || `Error ${response.status}`);
  return data;
};

/**
 * Poll page generation progress.
 * @param {string} trackingId
 * @returns {Promise<{progress, message, status, page_id?, page_slug?}>}
 */
export const getPageCreationProgress = async (trackingId) => {
  const response = await fetch(`${API_URL}/chat/page-progress/${trackingId}`);
  if (!response.ok) throw new Error(`Progress check failed: ${response.status}`);
  return response.json();
};
